
/**
 * Performance optimization utilities for the application
 */

// Debounce utility for search and input fields
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle utility for scroll events
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Memory efficient array chunking for large datasets
export const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

// Virtual scrolling calculation helper
export const calculateVisibleItems = (
  scrollTop: number,
  itemHeight: number,
  containerHeight: number,
  totalItems: number,
  buffer: number = 5
) => {
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
  const endIndex = Math.min(
    totalItems - 1,
    Math.ceil((scrollTop + containerHeight) / itemHeight) + buffer
  );
  
  return { startIndex, endIndex, visibleCount: endIndex - startIndex + 1 };
};

// Memoization utility for expensive computations
export const memoize = <T extends (...args: any[]) => any>(fn: T): T => {
  const cache = new Map();
  
  return ((...args: Parameters<T>) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn(...args);
    cache.set(key, result);
    return result;
  }) as T;
};

// Image lazy loading helper
export const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {}
) => {
  if (typeof window === 'undefined' || !window.IntersectionObserver) {
    return null;
  }
  
  return new IntersectionObserver(callback, {
    rootMargin: '50px',
    threshold: 0.1,
    ...options,
  });
};

// Image preloading utility
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// Bundle optimization utilities
export const preloadRoutes = (routes: Array<() => Promise<any>>) => {
  // Preload routes during idle time
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      routes.forEach(route => route());
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      routes.forEach(route => route());
    }, 2000);
  }
};

// Critical resource preloading for better Core Web Vitals
export const preloadCriticalResources = () => {
  // Only preload the logo - let components handle their own images with fetchPriority
  const criticalImages = [
    '/assets/mappa-caeli-logo.png'
  ];
  
  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
  
  // Preload critical fonts if not already loaded
  const criticalFonts = [
    'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&display=swap',
    'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&display=swap'
  ];
  
  criticalFonts.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'style';
      link.href = href;
      link.onload = () => {
        link.rel = 'stylesheet';
      };
      document.head.appendChild(link);
    }
  });
};

// Component preloading for critical user paths
export const preloadCriticalComponents = async (
  componentLoaders: Array<() => Promise<any>>
) => {
  try {
    await Promise.all(componentLoaders.map(loader => loader()));
  } catch (error) {
    console.warn('Failed to preload some components:', error);
  }
};

// Batch image preloading
export const preloadImages = async (urls: string[]): Promise<void> => {
  await Promise.all(urls.map(url => preloadImage(url)));
};
